import {
  Dropdown,
  dropdownTypes,
  Modal,
  NewButton,
  NewButtonVariant,
} from "@visiblethread/react-components";
import Field, { fieldTypes } from "components/Field";
import { licenseTypeLabel } from "constants/licenseTypes";
import { modalTypes } from "constants/types";
import React, { useId, useState } from "react";
import { useDispatch } from "react-redux";
import { hideModal } from "slices/app/actions/modals";
import { removeErrorMessage } from "slices/app/errorSlice";
import { createWorkspace } from "slices/systemAdmin/actions/systemAdmin";
import {
  DATE_VARIANTS,
  expiryDateValidator,
  formatDate,
  isWorkspaceNameValid,
} from "utils";

import {
  getOptionButton,
  LICENSE_TYPE_OPTIONS,
} from "./licenseTypeDropdown";

const oneWeekFromNow = () => {
  const date = new Date();
  date.setDate(date.getDate() + 7);
  return date;
};

const CreateWorkspaceWithLicensingModal = () => {
  const dispatch = useDispatch();
  const [workspaceName, setWorkspaceName] = useState("");
  const [workspaceNameError, setWorkspaceNameError] = useState("");
  const [licenseType, setLicenseType] = useState("Docs - Internal");
  const [numberUsers, setNumberUsers] = useState("1");
  const [notes, setNotes] = useState("");
  const [numberUsersError, setNumberUsersError] = useState("");
  const [expiryDate, setExpiryDate] = useState(oneWeekFromNow());
  const [expiryDateError, setExpiryDateError] = useState("");
  const notesId = useId();
  const workspaceElementId = useId;

  const handleWorkspaceNameChange = ({ target: { value } }) => {
    if (value === "") {
      setWorkspaceNameError("Cannot be empty");
    } else if (!isWorkspaceNameValid(value)) {
      setWorkspaceNameError(
        "Workspace name must only contain alphanumeric characters and following special characters: ()+-."
      );
    } else {
      setWorkspaceNameError("");
    }
    setWorkspaceName(value);
  };

  const handleNotesChange = ({ target: { value } }) => {
    setNotes(value);
  };

  const isDisabled = () =>
    numberUsersError !== "" ||
    expiryDateError !== "" ||
    workspaceName === "" ||
    workspaceNameError !== "";

  const handleClose = () => {
    dispatch(hideModal(modalTypes.CREATE_WORKSPACE_WITH_LICENSING));
  };

  const onSubmit = () => {
    dispatch(removeErrorMessage());
    dispatch(
      createWorkspace(
        {
          workspaceName,
          licenseType,
          numberUsers,
          expiryDate: formatDate(expiryDate, DATE_VARIANTS.ISO),
          notes,
        },
        true
      )
    );
  };

  return (
    <Modal
      data-testid="create-workspace-licensing-modal"
      className="fade-in create_workspace_licensing_modal"
      show
      onDismiss={handleClose}
      backdrop
      centered
    >
      <Modal.Header>
        <b>Create Workspace</b>
      </Modal.Header>
      <Modal.Body>
        <label
          htmlFor={`${workspaceElementId}_form_field-workspace-name`}
          className="form__field__general"
        >
          <b>Workspace Name</b>
          <input
            type="text"
            id={`${workspaceElementId}_form_field-workspace-name`}
            value={workspaceName}
            onChange={handleWorkspaceNameChange}
            className={workspaceNameError && "input__error"}
            autoFocus
          />

          <span
            className={`input__error__message ${workspaceNameError && "show"}`}
          >
            {workspaceNameError}
          </span>
        </label>
        <div className="form__field__license_type">
          <b>License Type</b>
          <Dropdown
            type={dropdownTypes.buttons}
            propsObject={{
              text: licenseTypeLabel(licenseType),
              options: LICENSE_TYPE_OPTIONS.map((o) =>
                getOptionButton({ option: o, setLicenseType })
              ),
              buttonVariant: "l2",
            }}
          />
        </div>
        <Field
          type={fieldTypes.integer}
          propsObject={{
            value: numberUsers,
            setValue: setNumberUsers,
            setValueError: setNumberUsersError,
            valueError: numberUsersError,
            labelText: "Number Users",
          }}
        />

        <Field
          type={fieldTypes.date}
          propsObject={{
            date: expiryDate || new Date(),
            setDate: setExpiryDate,
            dateError: expiryDateError,
            setDateError: setExpiryDateError,
            labelText: "Expiry Date",
            validator: expiryDateValidator,
          }}
        />

        <label htmlFor={notesId} className="form__field__notes">
          <b>Notes</b>
          <textarea
            id={notesId}
            data-testid="notes-input"
            rows={4}
            value={notes}
            onChange={handleNotesChange}
          />
        </label>
      </Modal.Body>
      <Modal.Footer>
        <NewButton
          data-testid="cancel-button"
          variant={NewButtonVariant.l2}
          onClick={handleClose}
        >
          Cancel
        </NewButton>

        <NewButton
          variant={NewButtonVariant.l1}
          onClick={onSubmit}
          disabled={isDisabled()}
          data-testid="ok-button"
        >
          Ok
        </NewButton>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateWorkspaceWithLicensingModal;
